'use client'

import { PatternDrawing } from '../types/drawing'

export interface TradingRecommendation {
  action: 'BUY' | 'SELL' | 'HOLD' | 'WAIT' | 'CAUTION'
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  reasoning: string
  targetPrice?: number
  stopLoss?: number
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH'
  timeframe: string
}

export const generateTradingRecommendation = (
  patterns: PatternDrawing[],
  currentPrice: number
): TradingRecommendation => {
  
  if (patterns.length === 0) {
    return {
      action: 'HOLD',
      confidence: 'LOW',
      reasoning: 'No clear patterns detected. Monitor for pattern formation.',
      riskLevel: 'MEDIUM',
      timeframe: 'Wait for confirmation'
    }
  }

  const primaryPattern = patterns[0]
  
  switch (primaryPattern.type) {
    case 'head-and-shoulders':
      const isInverted = 'isInverted' in primaryPattern && primaryPattern.isInverted
      if (isInverted) {
        return {
          action: 'BUY',
          confidence: 'HIGH',
          reasoning: 'Inverse Head & Shoulders - Strong bullish reversal signal',
          targetPrice: currentPrice * 1.15,
          stopLoss: currentPrice * 0.95,
          riskLevel: 'MEDIUM',
          timeframe: '2-4 weeks'
        }
      } else {
        return {
          action: 'SELL',
          confidence: 'HIGH',
          reasoning: 'Head & Shoulders - Strong bearish reversal signal',
          targetPrice: currentPrice * 0.85,
          stopLoss: currentPrice * 1.05,
          riskLevel: 'MEDIUM',
          timeframe: '2-4 weeks'
        }
      }
    
    case 'double-top':
      return {
        action: 'SELL',
        confidence: 'HIGH',
        reasoning: 'Double Top - Bearish reversal at resistance',
        targetPrice: currentPrice * 0.88,
        stopLoss: currentPrice * 1.03,
        riskLevel: 'MEDIUM',
        timeframe: '1-3 weeks'
      }
    
    case 'double-bottom':
      return {
        action: 'BUY',
        confidence: 'HIGH',
        reasoning: 'Double Bottom - Bullish reversal at support',
        targetPrice: currentPrice * 1.12,
        stopLoss: currentPrice * 0.97,
        riskLevel: 'MEDIUM',
        timeframe: '1-3 weeks'
      }
    
    case 'ascending-triangle':
      return {
        action: 'BUY',
        confidence: 'MEDIUM',
        reasoning: 'Ascending Triangle - Bullish continuation pattern',
        targetPrice: currentPrice * 1.10,
        stopLoss: currentPrice * 0.96,
        riskLevel: 'LOW',
        timeframe: '2-4 weeks'
      }
    
    case 'descending-triangle':
      return {
        action: 'SELL',
        confidence: 'MEDIUM',
        reasoning: 'Descending Triangle - Bearish continuation pattern',
        targetPrice: currentPrice * 0.90,
        stopLoss: currentPrice * 1.04,
        riskLevel: 'LOW',
        timeframe: '2-4 weeks'
      }
    
    case 'bull-flag':
      return {
        action: 'BUY',
        confidence: 'HIGH',
        reasoning: 'Bull Flag - Strong continuation signal after consolidation',
        targetPrice: currentPrice * 1.06,
        stopLoss: currentPrice * 0.98,
        riskLevel: 'LOW',
        timeframe: '1-2 weeks'
      }
    
    case 'bear-flag':
      return {
        action: 'SELL',
        confidence: 'HIGH',
        reasoning: 'Bear Flag - Bearish continuation after consolidation',
        targetPrice: currentPrice * 0.94,
        stopLoss: currentPrice * 1.02,
        riskLevel: 'LOW',
        timeframe: '1-2 weeks'
      }
    
    default:
      return {
        action: 'WAIT',
        confidence: 'MEDIUM',
        reasoning: 'Pattern detected - Monitor for confirmation',
        riskLevel: 'MEDIUM',
        timeframe: 'Monitor development'
      }
  }
} 